import { SearchSortAttribute } from "../api/adsApi/adsApi.interfaces";
import { ALL_FAMILIES, allPackagedInsightScope } from "../constants/filters";
import { FeatureFlags } from "../store/featureFlags";
import type { DiscoveryFilterState } from "../types/filters/common";
import { InsightFamiliesFilterField } from "../types/filters/insightFamilies";
import { InsightsFromAIFilterField } from "../types/filters/insightFromAI";
import { InsightPackagesFilterField } from "../types/filters/insightPackages";
import { MarketingNarrativesFilterField } from "../types/filters/marketingNarratives";
import { OnDemandDecksFilterField } from "../types/filters/onDemandDeckTemplates";
import { UnifiedFilterField } from "../types/filters/unified";
import type { IHubContentSubType } from "../types/ihubContentType";
import { IHubContentType } from "../types/ihubContentType";
import {
    InsightFamilyName,
    MarketingContentType,
    IHubContentProvider,
    InsightDeckTemplateTheme,
} from "../types/providers";
import { getMarketingNarrativeFiltersOptionsFromCategories } from "../utils/categories.utils";
import { isAccountAgnosticId } from "../utils/permission.utils";

import {
    defaultInsightFamiliesFilters,
    insightFamiliesFilters,
} from "./filters/insightFamilies";
import {
    defaultInsightPackagesFilters,
    insightPackagesFilters,
} from "./filters/insightPackages";
import {
    defaultInsightsFromAIFilters,
    insightsFromAIFilters,
} from "./filters/insightsFromAI";
import { marketingNarrativesFilters } from "./filters/marketingNarratives";
import {
    defaultOnDemandDecksFilters,
    onDemandDecksFilters,
} from "./filters/onDemandDeckTemplates";
import { defaultUnifiedFilters, unifiedFilters } from "./filters/unified";

export const DEFAULT_MARKETING_NARRATIVE_SEARCH_QUERY = " "; // has to be a space

export const defaultContentType = IHubContentType.INSIGHTS;

export const defaultContentSubTypeForInsight: IHubContentSubType = {
    provider: IHubContentProvider.INSIGHT_FAMILIES,
    scope: ALL_FAMILIES,
};

// Feature flag store loads after this, so we have to manually retrieve it
const uatPackagedInsight = new URLSearchParams(window.location.search).get(
    FeatureFlags.UAT_PACKAGED_INSIGHT,
);
export const defaultContentSubTypeForInsightInUniversalView: IHubContentSubType =
    !uatPackagedInsight
        ? {
              provider: IHubContentProvider.UNIFIED,
              scope: allPackagedInsightScope.value,
          }
        : {
              provider: IHubContentProvider.TEMPLATES,
              scope: InsightDeckTemplateTheme.AD_PRODUCTS,
          };

export const defaultContentSubTypeForMarketingContent: IHubContentSubType = {
    provider: IHubContentProvider.MARKETING_NARRATIVES,
    scope: MarketingContentType.ALL,
};

export const getDefaultContentSubType = (
    contentType: IHubContentType,
): IHubContentSubType => {
    if (contentType === IHubContentType.MARKETING_CONTENT)
        return defaultContentSubTypeForMarketingContent;
    return isAccountAgnosticId()
        ? defaultContentSubTypeForInsightInUniversalView
        : defaultContentSubTypeForInsight;
};

export const initialFilterState: DiscoveryFilterState = {
    contentType: defaultContentType,
    scopeToLabel: {},
    [IHubContentType.INSIGHTS]: {
        contentSubType: getDefaultContentSubType(IHubContentType.INSIGHTS),
        filters: {
            [IHubContentProvider.UNIFIED]: {
                fieldSelections: {
                    [UnifiedFilterField.BRAND]: [],
                    [UnifiedFilterField.BROWSE_NODE]: [],
                    [UnifiedFilterField.OBJECTIVE]: [],
                    [UnifiedFilterField.TOPIC]: [],
                    [UnifiedFilterField.LANGUAGE]: [],
                },
                filterOptions: {
                    [UnifiedFilterField.BRAND]: [],
                    [UnifiedFilterField.BROWSE_NODE]: [],
                    [UnifiedFilterField.OBJECTIVE]: [],
                    [UnifiedFilterField.TOPIC]: [],
                    [UnifiedFilterField.LANGUAGE]: [],
                },
                data: [],
                visibleFilters: defaultUnifiedFilters,
                additionalFilters: unifiedFilters,
                searchQuery: "",
            },
            [IHubContentProvider.INSIGHT_FAMILIES]: {
                typeOptions: [
                    ALL_FAMILIES,
                    ...Object.values(InsightFamilyName),
                ],
                fieldSelections: {
                    [InsightFamiliesFilterField.BRAND]: [],
                    [InsightFamiliesFilterField.BROWSE_NODE]: [],
                    [InsightFamiliesFilterField.OBJECTIVE]: [],
                },
                filterOptions: {
                    [InsightFamiliesFilterField.BRAND]: [],
                    [InsightFamiliesFilterField.BROWSE_NODE]: [],
                    [InsightFamiliesFilterField.OBJECTIVE]: [],
                },
                data: [],
                visibleFilters: defaultInsightFamiliesFilters,
                additionalFilters: insightFamiliesFilters,
                searchQuery: "",
            },
            [IHubContentProvider.INSIGHTS_FROM_AI]: {
                fieldSelections: {
                    [InsightsFromAIFilterField.CONTENT_TYPES]: [],
                    [InsightsFromAIFilterField.BRAND]: [],
                    [InsightsFromAIFilterField.BROWSE_NODE]: [],
                },
                filterOptions: {
                    [InsightsFromAIFilterField.CONTENT_TYPES]: [],
                    [InsightsFromAIFilterField.BRAND]: [],
                    [InsightsFromAIFilterField.BROWSE_NODE]: [],
                },
                data: [],
                visibleFilters: defaultInsightsFromAIFilters,
                additionalFilters: insightsFromAIFilters,
            },
            [IHubContentProvider.PACKAGES]: {
                fieldSelections: {
                    [InsightPackagesFilterField.BRAND]: [],
                    [InsightPackagesFilterField.BROWSE_NODE]: [],
                    [InsightPackagesFilterField.OBJECTIVE]: [],
                },
                filterOptions: {
                    [InsightPackagesFilterField.BRAND]: [],
                    [InsightPackagesFilterField.BROWSE_NODE]: [],
                    [InsightPackagesFilterField.OBJECTIVE]: [],
                },
                data: [],
                visibleFilters: defaultInsightPackagesFilters,
                additionalFilters: insightPackagesFilters,
                searchQuery: "",
            },
            [IHubContentProvider.TEMPLATES]: {
                fieldSelections: {
                    [OnDemandDecksFilterField.TOPIC]: [],
                    [OnDemandDecksFilterField.LANGUAGE]: [],
                },
                filterOptions: {
                    [OnDemandDecksFilterField.TOPIC]: [],
                    [OnDemandDecksFilterField.LANGUAGE]: [],
                },
                data: [],
                visibleFilters: defaultOnDemandDecksFilters,
                additionalFilters: onDemandDecksFilters,
                searchQuery: "",
            },
        },
    },
    [IHubContentType.MARKETING_CONTENT]: {
        contentSubType: defaultContentSubTypeForMarketingContent,
        filters: {
            [IHubContentProvider.MARKETING_NARRATIVES]: {
                // TODO: create additional filters list for marketing narratives
                fieldSelections: {
                    [MarketingNarrativesFilterField.MARKETPLACE]: [],
                    [MarketingNarrativesFilterField.LANGUAGE]: [],
                    [MarketingNarrativesFilterField.VERTICAL]: [],
                    [MarketingNarrativesFilterField.SUBVERTICAL]: [],
                    [MarketingNarrativesFilterField.AD_ACTIVATION_CHANNEL]: [],
                    [MarketingNarrativesFilterField.AD_PRODUCTS_AND_FORMATS]:
                        [],
                    [MarketingNarrativesFilterField.PRIMARY_CONTENT_CREATOR_TEAM]:
                        [],
                    [MarketingNarrativesFilterField.MARKETING_STRATEGY]: [],
                    [MarketingNarrativesFilterField.SEASONAL_EVENTS]: [],
                    [MarketingNarrativesFilterField.ADVERTISER_GOAL]: [],
                    [MarketingNarrativesFilterField.FILE_TYPE]: [],
                },
                filterOptions:
                    getMarketingNarrativeFiltersOptionsFromCategories(),
                data: [],
                visibleFilters: [],
                additionalFilters: marketingNarrativesFilters,
                searchQuery: DEFAULT_MARKETING_NARRATIVE_SEARCH_QUERY,
                sortAttribute: SearchSortAttribute.LAST_MODIFIED_DATE,
            },
        },
    },
};
